import React, { useEffect, useState, useMemo } from 'react';
import BranchService from '../../service/BranchService';
import { Modal, Form, Spinner} from 'react-bootstrap';
import { Trash2, Edit, Plus, MapPin, Building2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import '../../styles/view/ManageBranches.css';

export default function ManageBranches() {
    const [branches, setBranches]         = useState([]);
    const [loading, setLoading]           = useState(true);
    const [search, setSearch]             = useState('');
    const [filterName]     = useState('');
    const [filterUbicacion] = useState('');
    const [currentPage, setCurrentPage]   = useState(1);
    const pageSize = 5;
    const [showModal, setShowModal]       = useState(false);
    const [currentBranch, setCurrentBranch] = useState({ nombre: '', ubicacion: '' });
    const [isEditing, setIsEditing]       = useState(false);
  
    useEffect(() => { fetchData(); }, []);
  
    const fetchData = async () => {
      try {
        const data = await BranchService.getBranches();
        setBranches(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
  
    const handleDelete = async (id) => {
      if (window.confirm('¿Está seguro de eliminar esta sucursal?')) {
        try { await BranchService.deleteBranch(id); fetchData(); }
        catch (e) { console.error(e); }
      }
    };
  
    const handleEdit = (branch) => {
      setCurrentBranch(branch); setIsEditing(true); setShowModal(true);
    };
  
    const handleAdd = () => {
      setCurrentBranch({ nombre: '', ubicacion: '' }); setIsEditing(false); setShowModal(true);
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (isEditing) await BranchService.updateBranch(currentBranch.id, currentBranch);
        else           await BranchService.createBranch(currentBranch);
        setShowModal(false); fetchData();
      } catch (e) {
        console.error(e); alert('Error al guardar la sucursal.');
      }
    };
  
    const filteredBranches = useMemo(() => branches.filter(b => {
      const nombre    = (b.nombre    || '').toLowerCase();
      const ubicacion = (b.ubicacion || '').toLowerCase();
      return (
        nombre.includes(filterName.toLowerCase()) &&
        ubicacion.includes(filterUbicacion.toLowerCase()) &&
        (nombre.includes(search.toLowerCase()) || ubicacion.includes(search.toLowerCase()))
      );
    }), [branches, filterName, filterUbicacion, search]);
  
    const totalPages    = Math.max(1, Math.ceil(filteredBranches.length / pageSize));
    const paginatedData = useMemo(() => {
      const start = (currentPage - 1) * pageSize;
      return filteredBranches.slice(start, start + pageSize);
    }, [currentPage, filteredBranches]);
  
    if (loading) return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" style={{ color: 'var(--primary-color, #6366f1)' }} />
      </div>
    );
  
    return (
      <div className="mb-root">
        <div className="container-fluid p-3 p-md-4">
  
          {/* ── HEADER ── */}
          <div className="mb-header mb-4">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
              <div>
                <h3>Gestión de Sucursales</h3>
                <p className="mt-1">Administra las sucursales de la empresa</p>
              </div>
              <button className="mb-btn-add" onClick={handleAdd}>
                <Plus size={16} /><span>Nueva Sucursal</span>
              </button>
            </div>
          </div>
  
          {/* ── STAT CARDS ── */}
          <div className="row g-3 mb-4" style={{ marginTop: '1rem' }}>
            {[
              { label: 'Total Sucursales', value: branches.length,          icon: <Building2 size={20} color="var(--primary-color,#6366f1)" />, bg: 'rgba(99,102,241,.1)' },
              { label: 'Resultados',       value: filteredBranches.length,   icon: <MapPin     size={20} color="var(--success,#10b981)"       />, bg: 'rgba(16,185,129,.1)' },
              { label: 'Páginas',          value: totalPages,                icon: <Search     size={20} color="var(--info,#3b82f6)"           />, bg: 'rgba(59,130,246,.1)' },
            ].map((s, i) => (
              <div key={i} className="col-12 col-sm-4">
                <div className="mb-stat-card">
                  <div className="icon-wrap" style={{ background: s.bg }}>{s.icon}</div>
                  <div>
                    <div className="stat-value">{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
  
          {/* ── FILTER BAR ── */}
          <div className="mb-filter-bar mb-4">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-5">
                <label className="mb-filter-label">Búsqueda general</label>
                <div className="mb-search-wrap">
                  <Search size={15} className="search-icon" />
                  <input type="text" className="form-control"
                    placeholder="Buscar por nombre o ubicación…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} />
                </div>
              </div>
            </div>
          </div>
  
          {/* ── TABLE ── */}
          <div className="mb-table-card">
            <div className="table-responsive">
              <table className="align-middle">
                <thead>
                  <tr>
                    <th>Sucursal</th>
                    <th className="d-none d-sm-table-cell">Ubicación</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr><td colSpan="3">
                      <div className="mb-empty">
                        <div className="empty-icon"><Building2 size={48} /></div>
                        <p>No se encontraron sucursales</p>
                      </div>
                    </td></tr>
                  ) : paginatedData.map(branch => (
                    <tr key={branch.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="branch-icon"><Building2 size={17} /></div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '.875rem' }}>
                              {branch.nombre}
                            </div>
                            <div className="d-sm-none mt-1">
                              <span className="location-pill">
                                <MapPin size={11} /><span>{branch.ubicacion}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="d-none d-sm-table-cell">
                        <span className="location-pill">
                          <MapPin size={12} /><span>{branch.ubicacion}</span>
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-2">
                          <button className="mb-btn-action edit" onClick={() => handleEdit(branch)}>
                            <Edit size={13} /><span className="d-none d-md-inline">Editar</span>
                          </button>
                          <button className="mb-btn-action delete" onClick={() => handleDelete(branch.id)}>
                            <Trash2 size={13} /><span className="d-none d-md-inline">Eliminar</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
  
            {/* ── PAGINATION ── */}
            <div className="d-flex justify-content-between align-items-center px-4 py-3"
              style={{ borderTop: '1px solid var(--border-color, #e2e8f0)' }}>
              <span className="mb-pagination">
                {filteredBranches.length === 0
                  ? 'Sin resultados'
                  : `Mostrando ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, filteredBranches.length)} de ${filteredBranches.length}`}
              </span>
              <div className="mb-pagination d-flex align-items-center gap-2">
                <button className="btn-page" disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}>
                  <ChevronLeft size={14} />
                </button>
                <span style={{ fontWeight: 600 }}>Pág. {currentPage} / {totalPages}</span>
                <button className="btn-page" disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
  
        </div>
  
        {/* ── MODAL ── */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered className="mb-modal">
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditing ? <><Edit size={17} /> Editar Sucursal</> : <><Plus size={17} /> Nueva Sucursal</>}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nombre</Form.Label>
                <div className="input-with-icon">
                  <Building2 size={15} className="input-icon" />
                  <Form.Control type="text" placeholder="Ej: Sucursal Central"
                    value={currentBranch.nombre}
                    onChange={e => setCurrentBranch({ ...currentBranch, nombre: e.target.value })}
                    required />
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label>Ubicación</Form.Label>
                <div className="input-with-icon">
                  <MapPin size={15} className="input-icon" />
                  <Form.Control type="text" placeholder="Ej: San José, Costa Rica"
                    value={currentBranch.ubicacion}
                    onChange={e => setCurrentBranch({ ...currentBranch, ubicacion: e.target.value })}
                    required />
                </div>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
              <button type="submit" className="btn-save">Guardar</button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  }